import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  monolithTracePropagationTargets,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import { domainMeta } from './domainMeta';

// What each engine says when a fetch fails at the network layer: WebKit,
// Chromium, Gecko. The SDK appends the request host in parentheses.
const FETCH_NETWORK_ERROR =
  /^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.)(?: \(.*\))?$/;

/**
 * Drops a fetch network failure that carries no stack frame.
 *
 * The SDK stamps each fetch it wraps with its caller's stack, so a request this
 * bundle sends fails with this bundle's frames and still reports. A failure
 * with no frame at all came from a caller that has no script URL, such as a
 * script an in-app browser injects into the page. Those events say only that
 * the candidate's network dropped, and name no code to fix.
 */
export function dropFramelessNetworkError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.type === 'TypeError' &&
    FETCH_NETWORK_ERROR.test(exception.value ?? '') &&
    !exception.stacktrace?.frames?.length
  ) {
    return null;
  }

  return event;
}

// WebKit's message for a script reading across a frame boundary.
const CROSS_ORIGIN_FRAME_ERROR =
  /^Blocked a frame with origin "[^"]*" from accessing a cross-origin frame\./;

// A bundle file's URL, as opposed to the page URL an inline script reports.
const SCRIPT_FILE = /^[^?#\s]*\.[cm]?js(?:[?#]|$)/;

/**
 * Drops a cross-origin frame access that no bundle file took part in.
 *
 * The served page holds no inline script, so a frame whose file is the page
 * URL belongs to a script something else put there: an account's tracker or
 * landing snippet, or a script the browser injects. Such an error names no
 * code this app can fix. A frame from a bundle file keeps the event, so this
 * app's own cross-frame access still reports.
 */
export function dropInlineCrossOriginFrameError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];

  if (
    exception?.type === 'SecurityError' &&
    CROSS_ORIGIN_FRAME_ERROR.test(exception.value ?? '') &&
    !frames.some(frame => SCRIPT_FILE.test(frame.filename ?? ''))
  ) {
    return null;
  }

  return event;
}

// How the SDK reports a WebKit `AbortError` DOMException that carries the
// engine's default message and no stack.
const WEBKIT_DEFAULT_ABORT = 'AbortError: The operation was aborted.';

/**
 * Drops a stackless WebKit abort while the CameraTag recorder is on the page.
 *
 * CameraTag's script, loaded from cameratag.com by the video recording stage,
 * calls `play()` on its preview video without handling the promise. Its own
 * `pause()` interrupts a pending `play()`, and WebKit rejects that promise with
 * a stackless `AbortError`. The event names no frame and no code this app can
 * fix. The same abort on a page without CameraTag still reports.
 */
export function dropCameraTagAbortError(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.value === WEBKIT_DEFAULT_ABORT &&
    !exception.stacktrace?.frames?.length &&
    window.CameraTag
  ) {
    return null;
  }

  return event;
}

// What WebKit, then Chromium and Gecko, say when a script calls the Facebook
// pixel's global before the pixel defines it.
const MISSING_FACEBOOK_PIXEL =
  /^(?:Can't find variable: fbq|fbq is not defined)$/;

/**
 * Drops a call to an undefined Facebook pixel made by an inline script.
 *
 * An account's custom conversion tracker runs as an inline script, so its
 * frame is the page URL. A tracker that calls `fbq` without loading the pixel
 * first throws this on every page it runs on, and names no code this app can
 * fix. The built-in Facebook tracker defines `fbq` before it calls it. An
 * innermost frame from a bundle file keeps the event.
 */
export function dropInlineMissingFacebookPixelError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];

  if (
    exception?.type === 'ReferenceError' &&
    MISSING_FACEBOOK_PIXEL.test(exception.value ?? '') &&
    !SCRIPT_FILE.test(innermost?.filename ?? '')
  ) {
    return null;
  }

  return event;
}

// The namespace Firefox for iOS, and Brave for iOS built from it, gives the
// scripts it injects into every page.
const IOS_BROWSER_USER_SCRIPT = /__firefox__/;

/**
 * Drops an error that names that namespace when no bundle file took part in it.
 *
 * Brave for iOS injects scripts that read `window.__firefox__`, and on this
 * page they can run before the browser defines it. They report the page URL as
 * their file. Such an error names no code this app can fix. A frame from a
 * bundle file keeps the event.
 */
export function dropIosBrowserUserScriptError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];

  if (
    IOS_BROWSER_USER_SCRIPT.test(exception?.value ?? '') &&
    !frames.some(frame => SCRIPT_FILE.test(frame.filename ?? ''))
  ) {
    return null;
  }

  return event;
}

/**
 * Browser error reporting, tracing, profiling and logs for the applicant
 * portal.
 *
 * No session replay. The page URL here is the applicant's credential, and a
 * replay records it where nothing can scrub it: the rrweb meta event that opens
 * every snapshot carries `window.location.href` and reaches no SDK hook, and
 * the DOM snapshot keeps every portal link's `href`. Errors and traces still
 * say what broke, and `applicant.ref` says whose session it was.
 *
 * Dormant unless SENTRY_DSN is baked into the build. That gate is the whole
 * switch: a local `npm run start`, a Storybook build and a Jest run all leave it
 * unset and therefore start no client at all.
 *
 * One bundle serves every tenant, so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load.
 */

export function initSentry(): void {
  const dsn = process.env.SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name.
    release: process.env.COMMIT_SHA,
    tracesSampleRate: sampleRate(process.env.SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(process.env.SENTRY_PROFILES_SAMPLE_RATE, 1),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: process.env.SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Fed by `utils/sentryUser`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // These pages carry candidate PII. Nothing about a candidate reaches Sentry
    // that this app does not put there deliberately.
    sendDefaultPii: false,
    // Transcend's consent scripts fetch from their own hosts, and a blocked or
    // cancelled request rejects inside their code. The SDK appends the failed
    // host to the message, and each release groups the frames anew, so these
    // fill the project with copies of one cause this app cannot fix.
    // Their consent UI also rejects without a host when a translation file
    // fails to load. Filter by message, not by script URL: Transcend's airgap
    // wraps native DOM and network methods, so a script-URL filter would also
    // drop errors this app's own code raises through those wrappers.
    ignoreErrors: [
      /\((?:transcend-cdn\.com|telemetry\.transcend\.io)\)$/,
      /^Failed to load translations for language /,
    ],
    beforeSend: event => {
      const kept = dropFramelessNetworkError(event);
      const ownFrame = kept && dropInlineCrossOriginFrameError(kept);
      const noCameraTag = ownFrame && dropCameraTagAbortError(ownFrame);
      const noPixel =
        noCameraTag && dropInlineMissingFacebookPixelError(noCameraTag);
      return noPixel && dropIosBrowserUserScriptError(noPixel);
    },
  });

  Sentry.setTag('tenant', domainMeta.tenant);
}
